import * as bootstrap from 'bootstrap';
import * as addressautocomplete from 'smg/addressautocomplete';
import { PromptPasteAddress } from 'smg/admin/addresses';
import * as models from 'smg/admin/models';
import axios from 'smg/axios';
import * as countries from 'smg/countries';
import { ShowError } from 'smg/errors';
import { Phone } from 'smg/phone';
import * as template from 'smg/template';
import { RenderUsageMeta } from './cardusage';

export interface ShippingHandle {
    refresh(userID: string): void;
    // Snapshot of the current shipping selection for the place-order POST.
    // Returns the selected card's ShippingOptionID when the form still
    // matches that option (Normalize on the server will dedup via the ID),
    // otherwise the freshly typed/edited content for content-side dedup
    // through ShippingOption.Normalize → NewAddresses. Returns undefined when
    // skip-shipping is on or when the form is missing the minimum address
    // fields — same threshold as currentAddress().
    //
    // Async because the recipient phone field rides intlTelInput, which
    // normalizes to E.164 via a Promise (utils.js is lazy-loaded).
    snapshot(): Promise<models.OrderShippingOption | undefined>;
}

export interface ShippingDeps {
    // Fires whenever the shipping address materially changes (typing,
    // autocomplete pick, previous-card click, paste, clear, or skip toggle).
    // The argument is undefined when the form lacks the minimum fields the
    // tax handler needs (addressLine1/city/postalCode/country, plus stateID
    // for US) or when skip-shipping is on — callers should treat that as
    // "no address."
    onAddressChange?: (addrInit: models.IAddress | undefined) => void;
}

export function SetupShipping(deps?: ShippingDeps): ShippingHandle {
    const $skip = document.getElementById('shipping-skip') as HTMLInputElement;
    const $collapse = document.getElementById('shipping-form-collapse') as HTMLElement;
    const $fieldset = document.getElementById('shipping-fieldset') as HTMLFieldSetElement;

    const $fullName = document.getElementById('shipping-name') as HTMLInputElement;
    const $company = document.getElementById('shipping-company') as HTMLInputElement;
    const $country = document.getElementById('shipping-country') as HTMLSelectElement;
    const $state = document.getElementById('shipping-state') as HTMLSelectElement;
    const $address1 = document.getElementById('shipping-address1') as HTMLInputElement;
    const $address2 = document.getElementById('shipping-address2') as HTMLInputElement;
    const $city = document.getElementById('shipping-city') as HTMLInputElement;
    const $zip = document.getElementById('shipping-zip') as HTMLInputElement;
    // Recipient phone/email — separate from the contact section above, which
    // captures the customer's account contact. Shipping recipients can have
    // their own (e.g. drop-ship to a warehouse manager, gift to a third
    // party). Phone is required (carriers need it; collecting it here beats
    // chasing it down at ship time) — enforced by the input's `required` via
    // ReportValidity at submit, since ShippingOption.Validate doesn't require
    // it server-side. Email stays optional. Phone rides intlTelInput so the
    // persisted value lands in E.164.
    const $phone = document.getElementById('shipping-phone') as HTMLInputElement;
    const $email = document.getElementById('shipping-email') as HTMLInputElement;
    const phone = new Phone($phone);

    const $optionsRow = document.getElementById('shipping-options-row') as HTMLElement;
    const $optionsScroller = document.getElementById('shipping-options-scroller') as HTMLElement;
    const $paste = document.getElementById('shipping-paste') as HTMLButtonElement;
    const $clear = document.getElementById('shipping-clear') as HTMLButtonElement;

    const countriesReady = countries.Selects($country, $state, {
        defaultUnitedStates: true,
        stateParentSelector: '.state-field-wrapper',
    });
    void countriesReady.catch(ShowError);

    addressautocomplete.EnableAddressAutocomplete($address1, {
        city: $city,
        state: $state,
        zip: $zip,
        country: $country,
    });

    // The existing card the admin most recently picked, or undefined when
    // none picked yet. snapshot() returns its ID only when the form still
    // matches the card exactly — any edit (zip, name, state, etc.) falls
    // back to the content path so manual tweaks land on the order. We hold
    // the full card so the match check has every field to compare against;
    // a bare ID wouldn't be enough.
    let selectedShippingOption: models.HyejooShippingOption | undefined;

    function clearActive() {
        for (const $c of $optionsScroller.querySelectorAll<HTMLElement>('.shipping-option-card.active')) {
            $c.classList.remove('active');
        }
        selectedShippingOption = undefined;
    }

    function currentAddress(): models.Address | undefined {
        // Skip-shipping is a no-address signal — the tax handler treats a
        // missing addressLine1 as "don't compute tax," which is what we want.
        if ($skip.checked) {
            return undefined;
        }
        const addressLine1 = $address1.value.trim();
        const city = $city.value.trim();
        const postalCode = $zip.value.trim();
        const stateID = parseInt($state.value, 10) || 0;
        const countryID = parseInt($country.value, 10) || 0;
        if (!addressLine1 || !city || !postalCode || !countryID) {
            return undefined;
        }
        // Backend enforces required_if=CountryID 235 (US) — match here so we
        // don't spam the API with a request it'll just 400.
        if (countryID === models.CountryID.COUNTRIES_UNITED_STATES && !stateID) {
            return undefined;
        }
        // Include fullName/organization for downstream consumers that need
        // them (payment.setShippingDestination → Spreedly tokenize uses
        // these for the AVS billing payload). Tax compute ignores them.
        //
        // Phone/email ride as nested submodels so the billing form's
        // "same as shipping" path can inherit recipient contact info via
        // payment.setShippingDestination. The phone uses the raw typed
        // value; snapshot() awaits phone.format() for the E.164 normalized
        // version that actually lands on the persisted shippingoptions row.
        const recipientPhone = $phone.value.trim();
        const recipientEmail = $email.value.trim().toLowerCase();
        return new models.Address({
            fullName: $fullName.value.trim(),
            organization: $company.value.trim(),
            addressLine1,
            addressLine2: $address2.value.trim(),
            city,
            stateID,
            postalCode,
            countryID,
            phoneNumber: recipientPhone ? { phone: recipientPhone } : undefined,
            emailAddress: recipientEmail ? { email: recipientEmail } : undefined,
        });
    }

    function notifyAddressChange() {
        deps?.onAddressChange?.(currentAddress());
    }

    function fillForm(address: models.Address) {
        $fullName.value = address.fullName;
        $company.value = address.organization;
        $address1.value = address.addressLine1;
        $address2.value = address.addressLine2;
        $city.value = address.city;
        $zip.value = address.postalCode;
        // Address carries nested phoneNumber/emailAddress submodels. fillForm
        // is used by both PromptPasteAddress (which can carry contact info)
        // and a saved shipping option's address; blank when the source lacks
        // them.
        $email.value = address.emailAddress?.email ?? '';
        // intlTelInput's setNumber is async; the ready() gate waits for
        // utils.js to load so the E.164 input parses correctly. Mirrors the
        // pattern contact.ts uses for the customer phone — including the
        // clearinvalid dispatch, since the phone is required and setNumber
        // doesn't fire input/change for ReportValidity's revert listeners.
        void phone.ready().then(() => {
            phone.iti.setNumber(address.phoneNumber?.phone ?? '');
            $phone.dispatchEvent(new Event('clearinvalid'));
        });
        // Programmatic .value = doesn't fire input/change, so ReportValidity's
        // revert listeners wouldn't otherwise clear is-invalid state when the
        // admin picks an address-on-file. Dispatch clearinvalid so any
        // previously-flagged field drops its error feedback now that we've
        // just filled it.
        for (const $el of [$fullName, $company, $address1, $address2, $city, $zip, $email]) {
            $el.dispatchEvent(new Event('clearinvalid'));
        }

        // Country/state need countries.Selects to have built the option lists
        // first; the country change event rebuilds the state list, so the
        // state assignment has to wait until that's done.
        void countriesReady.then(() => {
            if (address.countryID) {
                $country.value = address.countryID.toString();
                $country.dispatchEvent(new Event('change'));
            }
            if (address.stateID) {
                $state.value = address.stateID.toString();
            }
            $country.dispatchEvent(new Event('clearinvalid'));
            $state.dispatchEvent(new Event('clearinvalid'));
            // Programmatic .value = doesn't fire input/change, so the
            // listeners below wouldn't pick this up. The country dispatch
            // notifies once, but state is set after — re-notify so the
            // address is complete when tax fetches.
            notifyAddressChange();
        });
    }

    function clearForm() {
        $fullName.value = '';
        $company.value = '';
        $address1.value = '';
        $address2.value = '';
        $city.value = '';
        $zip.value = '';
        $email.value = '';
        void phone.ready().then(() => phone.iti.setNumber(''));
        void countriesReady.then(() => {
            // Reset to the same default countries.Selects gave us on init so
            // the state list rebuilds and the form returns to its blank shape.
            $country.value = models.CountryID.COUNTRIES_UNITED_STATES.toString();
            $country.dispatchEvent(new Event('change'));
            $state.value = '';
            notifyAddressChange();
        });
        clearActive();
    }

    function applyShippingOption(so: models.HyejooShippingOption, $card: HTMLElement) {
        const address = so.address;
        fillForm(address);
        clearActive();
        $card.classList.add('active');
        selectedShippingOption = so;
    }

    $paste.addEventListener('click', () => {
        PromptPasteAddress().then(address => {
            fillForm(address);
            clearActive();
        }).catch(() => {
            // user cancelled
        });
    });

    $clear.addEventListener('click', () => clearForm());

    function renderOptions(options: models.HyejooShippingOption[]) {
        if (options.length === 0) {
            $optionsScroller.replaceChildren();
            $optionsRow.classList.add('d-none');
            return;
        }

        (() => {
            const f = document.createDocumentFragment();
            for (const so of options) {
                const $card = template.Clone('#shipping-option-card-template').firstElementChild as HTMLElement;
                const address = so.address;

                const $name = $card.querySelector('.shipping-option-name') as HTMLElement;
                const $addr = $card.querySelector('.shipping-option-address') as HTMLElement;
                const $meta = $card.querySelector('.shipping-option-meta') as HTMLElement;
                const $defaultBadge = $card.querySelector('.shipping-option-default-badge') as HTMLElement;

                $name.textContent = [address.fullName, address.organization].filter(Boolean).join(' · ') || address.formatStreetAddress() || 'Address';
                $addr.textContent = address.toOneLineShort();
                $defaultBadge.classList.toggle('d-none', !so.default);

                RenderUsageMeta($meta, so.orders);

                $card.addEventListener('click', () => applyShippingOption(so, $card));
                $card.addEventListener('keydown', e => {
                    // Don't hijack Enter/Space when focus is on the nested order
                    // link — the link should follow its href, not select the card.
                    if (e.target !== $card) {
                        return;
                    }
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        applyShippingOption(so, $card);
                    }
                });
                f.appendChild($card);
            }
            $optionsScroller.replaceChildren(f);
            $optionsRow.classList.toggle('d-none', $skip.checked);
        })();
    }

    let fetchSeq = 0;
    function fetchOptions(userID: string) {
        const mySeq = ++fetchSeq;
        // A customer switch invalidates the picked card — its ID belongs to
        // the previous user, so reset before the new fetch lands. Edit-then-
        // place is handled separately at snapshot() time via the form-vs-card
        // match check; this only covers the customer-switch path.
        selectedShippingOption = undefined;
        // Clear immediately on every refresh so a previous customer's cards
        // never linger during the network roundtrip or get stranded if the
        // new fetch errors out.
        renderOptions([]);
        if (!userID) {
            return;
        }
        void axios.get('/v2/shipping-options', {
            params: {
                // active:true excludes soft-deleted rows — hyejoo has no
                // implicit default for this and we don't want to surface
                // addresses the customer has already removed.
                'q': `user:${userID} active:true`,
                'data': 'id,address,orders,lastOrdered,default',
                // default desc surfaces the user-picked default address first
                // (Label='Default'); orders desc tiebreaks by most-recent use
                // so the next-most-likely option follows.
                'sort': 'default desc,orders desc',
                'max': 50,
            },
        }).then(resp => {
            if (mySeq !== fetchSeq) {
                return;
            }
            renderOptions(models.HyejooShippingOptionsResponse.fromJSON(resp.data).results);
        }).catch((err: unknown) => {
            if (mySeq !== fetchSeq) {
                return;
            }
            ShowError(err);
        });
    }

    // Address changes fire notify so consumers (tax, payment) can react.
    // Tax ignores name/company; payment now uses them as the cardholder
    // name + company on same-as-shipping tokenization, so they need to
    // propagate too — otherwise editing the name AFTER the address is
    // valid would leave Spreedly with a stale empty cardholder name on
    // submit. Phone/email aren't on the Address payload, but they're part
    // of the snapshot fingerprint — listeners include them so a typed
    // edit after a card pick drops selectedShippingOption via the same
    // path manual address edits use.
    for (const $el of [$fullName, $company, $address1, $address2, $city, $zip, $phone, $email]) {
        $el.addEventListener('input', notifyAddressChange);
    }
    for (const $el of [$country, $state]) {
        $el.addEventListener('change', notifyAddressChange);
    }

    const collapse = bootstrap.Collapse.getOrCreateInstance($collapse, { toggle: false });
    $skip.addEventListener('change', () => {
        $fieldset.disabled = $skip.checked;
        if ($skip.checked) {
            collapse.hide();
            $optionsRow.classList.add('d-none');
        } else {
            collapse.show();
            if ($optionsScroller.children.length > 0) {
                $optionsRow.classList.remove('d-none');
            }
        }
        // Toggling skip flips currentAddress() between undefined and the form
        // contents — tax should match.
        notifyAddressChange();
    });

    function formMatchesCard(
        addr: models.Address,
        recipientPhone: string,
        recipientEmail: string,
        card: models.HyejooShippingOption,
    ): boolean {
        // Compare what we'd persist — the same fields the server's
        // Address.Normalize dedupes on, plus the recipient phone/email
        // on the nested address. A match here guarantees the server would
        // resolve to the same canonical shippingoptions row the ID already
        // points at.
        const cardAddress = card.address;
        if (!cardAddress) {
            return false;
        }
        return (cardAddress.fullName ?? '') === addr.fullName &&
            (cardAddress.organization ?? '') === addr.organization &&
            (cardAddress.addressLine1 ?? '') === addr.addressLine1 &&
            (cardAddress.addressLine2 ?? '') === addr.addressLine2 &&
            (cardAddress.city ?? '') === addr.city &&
            (cardAddress.stateID ?? 0) === addr.stateID &&
            (cardAddress.postalCode ?? '') === addr.postalCode &&
            (cardAddress.countryID ?? 0) === addr.countryID &&
            (cardAddress.phoneNumber?.phone ?? '') === recipientPhone &&
            (cardAddress.emailAddress?.email ?? '') === recipientEmail;
    }

    async function snapshot(): Promise<models.OrderShippingOption | undefined> {
        const addr = currentAddress();
        if (!addr) {
            return undefined;
        }
        // intlTelInput's setNumber (from fillForm after a card pick) only
        // runs after ready() settles. If the admin picks a card and
        // clicks place-order before utils.js finishes loading, $phone.value
        // is still empty even though a card phone was supplied — we'd then
        // see "no phone" and miss the card-match path, posting phone:''
        // instead of the ID. Await the same gate fillForm uses so we always
        // observe the post-setNumber input value.
        await phone.ready();
        // intlTelInput normalizes the typed digits to E.164 once utils.js
        // has loaded; awaiting format() here keeps the persisted value
        // canonical so Address.Normalize dedupes correctly across orders.
        const recipientPhone = $phone.value.trim() ? await phone.format() : '';
        const recipientEmail = $email.value.trim().toLowerCase();

        // Picked card AND the form still matches it: send just the ID so
        // the server skips Normalize's NewShippingOptions materialization
        // path. Any drift (manual edit, paste, etc.) falls through to the
        // content path below so the admin's tweaks land on the order
        // instead of getting silently swallowed.
        if (selectedShippingOption && formMatchesCard(addr, recipientPhone, recipientEmail, selectedShippingOption)) {
            return new models.OrderShippingOption({ shippingOptionID: selectedShippingOption.id });
        }
        // UserID is intentionally omitted — the POST /orders handler stamps
        // it from the order's UserID before NewShippingOptions sees the
        // submodel, so checkout doesn't need to plumb it through.
        return new models.OrderShippingOption({
            shippingOption: {
                address: {
                    fullName: addr.fullName,
                    organization: addr.organization,
                    addressLine1: addr.addressLine1,
                    addressLine2: addr.addressLine2,
                    city: addr.city,
                    stateID: addr.stateID,
                    postalCode: addr.postalCode,
                    countryID: addr.countryID,
                    phoneNumber: recipientPhone ? { phone: recipientPhone } : undefined,
                    emailAddress: recipientEmail ? { email: recipientEmail } : undefined,
                },
            },
        });
    }

    return {
        refresh: fetchOptions,
        snapshot,
    };
}
